import { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Check,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { VcfWorkstream, VcfCapability } from '@/hooks/useWorkstreams';

interface WorkstreamTreeProps {
  workstreams: VcfWorkstream[];
  selectedCapabilities: Set<string>;
  onCapabilitySelect?: (capabilityId: string | null) => void;
  selectedCapabilityId?: string | null;
  onCapabilityToggle?: (capabilityId: string, included: boolean) => void;
}

export function WorkstreamTree({
  workstreams,
  selectedCapabilities,
  onCapabilitySelect,
  selectedCapabilityId,
  onCapabilityToggle,
}: WorkstreamTreeProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedWorkstreams, setExpandedWorkstreams] = useState<Set<string>>(new Set());

  // Filter workstreams by search query
  const filteredWorkstreams = useMemo(() => {
    if (!searchQuery.trim()) return workstreams;

    const query = searchQuery.toLowerCase();
    return workstreams.filter(
      (ws) =>
        ws.name.toLowerCase().includes(query) ||
        ws.code.toLowerCase().includes(query) ||
        ws.capabilities?.some(
          (c) =>
            c.name.toLowerCase().includes(query) ||
            c.code.toLowerCase().includes(query)
        )
    );
  }, [workstreams, searchQuery]);

  const toggleWorkstream = (workstreamId: string) => {
    const newExpanded = new Set(expandedWorkstreams);
    if (newExpanded.has(workstreamId)) {
      newExpanded.delete(workstreamId);
    } else {
      newExpanded.add(workstreamId);
    }
    setExpandedWorkstreams(newExpanded);
  };

  const getCapabilityCount = (workstream: VcfWorkstream): { selected: number; total: number } => {
    const capabilities = workstream.capabilities || [];
    const selected = capabilities.filter((c) => selectedCapabilities.has(c.id)).length;
    return { selected, total: capabilities.length };
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-heading text-sm font-semibold text-xtravirt-dark uppercase tracking-wide">
          Workstreams
        </h3>
        {/* Search */}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-9 py-1.5 w-full text-sm"
          />
        </div>
      </div>

      {/* Tree Content - Flat list of workstreams */}
      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {filteredWorkstreams.map((ws) => {
          const isExpanded = expandedWorkstreams.has(ws.id);
          const count = getCapabilityCount(ws);
          const hasSelectedCapabilities = count.selected > 0;

          return (
            <div key={ws.id}>
              {/* Workstream Row */}
              <button
                onClick={() => toggleWorkstream(ws.id)}
                className={cn(
                  'flex items-center justify-between w-full px-2 py-2 rounded-md text-left transition-colors text-sm',
                  hasSelectedCapabilities ? 'bg-green-50/50' : 'hover:bg-gray-50'
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  )}
                  <span
                    className={cn(
                      'truncate',
                      hasSelectedCapabilities ? 'text-xtravirt-dark font-medium' : 'text-gray-700'
                    )}
                  >
                    {ws.name}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {hasSelectedCapabilities && (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  )}
                  <span
                    className={cn(
                      'text-xs',
                      hasSelectedCapabilities ? 'text-green-600 font-medium' : 'text-gray-400'
                    )}
                  >
                    {count.selected}/{count.total}
                  </span>
                </div>
              </button>

              {/* Capabilities */}
              {isExpanded && ws.capabilities && (
                <div className="ml-6 mt-0.5 space-y-0.5">
                  {ws.capabilities.map((cap) => {
                    const isSelected = selectedCapabilities.has(cap.id);
                    const isActive = selectedCapabilityId === cap.id;

                    return (
                      <CapabilityTreeItem
                        key={cap.id}
                        capability={cap}
                        isSelected={isSelected}
                        isActive={isActive}
                        onSelect={() => onCapabilitySelect?.(cap.id)}
                        onToggle={
                          onCapabilityToggle
                            ? () => onCapabilityToggle(cap.id, !isSelected)
                            : undefined
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-xtravirt-dark">{selectedCapabilities.size}</span>
          {' '}capabilities selected
        </div>
      </div>
    </div>
  );
}

// ============ Capability Tree Item ============

interface CapabilityTreeItemProps {
  capability: VcfCapability;
  isSelected: boolean;
  isActive: boolean;
  onSelect?: () => void;
  onToggle?: () => void;
}

function CapabilityTreeItem({
  capability,
  isSelected,
  isActive,
  onSelect,
  onToggle,
}: CapabilityTreeItemProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1 rounded-md text-xs transition-colors cursor-pointer',
        isActive ? 'bg-xtravirt-cyan/10 border border-xtravirt-cyan/30' : 'hover:bg-gray-100',
        isSelected && !isActive && 'bg-green-50'
      )}
      onClick={onSelect}
    >
      {/* Checkbox */}
      {onToggle && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={cn(
            'flex h-4 w-4 items-center justify-center rounded border transition-colors flex-shrink-0',
            isSelected
              ? 'border-green-500 bg-green-500 text-white'
              : 'border-gray-300 bg-white hover:border-gray-400'
          )}
        >
          {isSelected && <Check className="h-2.5 w-2.5" />}
        </button>
      )}

      {/* Capability Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-gray-500">{capability.code}</span>
          {capability.cls && (
            <span
              className={cn(
                'px-1 py-0.5 rounded text-[10px] font-medium',
                capability.cls === 'CORE' && 'bg-red-100 text-red-700',
                capability.cls === 'RECOMMENDED' && 'bg-yellow-100 text-yellow-700',
                capability.cls === 'OPTIONAL' && 'bg-gray-100 text-gray-600'
              )}
            >
              {capability.cls === 'CORE' ? 'C' : capability.cls === 'RECOMMENDED' ? 'R' : 'O'}
            </span>
          )}
        </div>
        <p className="text-gray-600 truncate mt-0.5">{capability.name}</p>
      </div>

      {/* Base Days indicator */}
      {capability.activities && capability.activities.length > 0 && (
        <span className="text-gray-400 text-[10px] flex-shrink-0">
          {(capability.activities.reduce((sum, a) => sum + a.baseDays, 0) / 100).toFixed(1)}d
        </span>
      )}
    </div>
  );
}

export default WorkstreamTree;
